import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import API_BASE_URL from "./config";
import "./Reminders.css";

function Reminders() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [reminders, setReminders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState("reminders");
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [remRes, apptRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/ai/reminders`, { headers }),
        fetch(`${API_BASE_URL}/api/ai/appointments`, { headers })
      ]);
      if (remRes.ok) { const d = await remRes.json(); setReminders(d.reminders || []); }
      if (apptRes.ok) { const d = await apptRes.json(); setAppointments(d.appointments || []); }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (d) => {
    if (!d) return "Not set";
    return new Date(d).toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" });
  };

  const formatTime = (t) => {
    if (!t) return "Not set";
    const [h, m] = t.split(":");
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour}:${m} ${hour >= 12 ? "PM" : "AM"}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "#f6ad55";
      case "scheduled": return "#68d391";
      case "completed": return "#a0aec0";
      default: return "#667eea";
    }
  };

  const items = activeTab === "reminders" ? reminders : appointments;

  return (
    <div className="rem-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="rem-main">
        <header className="rem-header">
          <div className="rem-header-left">
            <button className="rem-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h2>⏰ Reminders & Appointments</h2>
          </div>
          <button className="rem-ai-btn" onClick={() => navigate("/ai")}>
            🤖 Create with AI
          </button>
        </header>

        <div className="rem-tabs">
          <button className={`rem-tab ${activeTab === "reminders" ? "active" : ""}`} onClick={() => setActiveTab("reminders")}>
            ⏰ Reminders <span className="rem-tab-count">{reminders.length}</span>
          </button>
          <button className={`rem-tab ${activeTab === "appointments" ? "active" : ""}`} onClick={() => setActiveTab("appointments")}>
            📅 Appointments <span className="rem-tab-count">{appointments.length}</span>
          </button>
        </div>

        <div className="rem-content">
          {loading ? (
            <div className="rem-loading">Loading...</div>
          ) : items.length === 0 ? (
            <div className="rem-empty">
              <div className="rem-empty-icon">{activeTab === "reminders" ? "⏰" : "📅"}</div>
              <h3>No {activeTab} yet</h3>
              <p>Use Photentra AI to create {activeTab} with natural language.</p>
              <button className="rem-empty-btn" onClick={() => navigate("/ai")}>
                🤖 Open Photentra AI
              </button>
            </div>
          ) : (
            <div className="rem-grid">
              {items.map(item => (
                <div key={item.id} className={`rem-card ${item.status === "completed" ? "completed" : ""}`}>
                  <div className="rem-card-top">
                    <span className="rem-card-icon">{activeTab === "reminders" ? "⏰" : "📅"}</span>
                    <span className="rem-card-status" style={{ background: getStatusColor(item.status) + "22", color: getStatusColor(item.status) }}>
                      {item.status}
                    </span>
                  </div>
                  <h3 className="rem-card-title">{item.title}</h3>
                  {activeTab === "appointments" && item.client_name && (
                    <div className="rem-card-client">👤 {item.client_name}</div>
                  )}
                  <div className="rem-card-details">
                    <div className="rem-card-detail">
                      <span>📅</span>
                      <span>{formatDate(activeTab === "reminders" ? item.reminder_date : item.appointment_date)}</span>
                    </div>
                    <div className="rem-card-detail">
                      <span>🕐</span>
                      <span>{formatTime(activeTab === "reminders" ? item.reminder_time : item.appointment_time)}</span>
                    </div>
                  </div>
                  <div className="rem-card-created">
                    Created {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Reminders;
